import _ from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Reorder } from 'framer-motion';
import { generateClient } from "@aws-amplify/api";
import { getAgentSettings as _getAgentSettings, getDeals as _getDeals, getRecruitmentSources as _getRecruitmentSources, getStudies as _getStudies, getWorkflows as _getWorkflows } from '@/graphql/queries';
import { getSegments as _getSegments } from '@/graphql/small-queries';
import { upsertWorkflow as _upsertWorkflow } from '@/graphql/mutations';
import "./WorkflowBuilder.scss";
import TaskModal from "./TaskModal";
import { selectField, textField } from '@/commonFields';
import Breadcrumb from '@/Breadcrumb';
import NameField from './NameField';
import { actions, days, delay, times } from './const';
import RootContext from '@/RootContext';
import Header from '@/Header';
import AutoTextarea from '@/components/AutoTextarea';

const client = generateClient();

const getAgentSettings = ({ id } = {}) => {
    return client.graphql({ query: _getAgentSettings, variables: { id, sorting: { field: 'agent_name', order: 'asc' } } })
        .then(response => {
            const data = response.data.getAgentSettings.agent_settings;
            if (!data) throw new Error('AgentSettings not found');
            return data;
        });
};

const getWorkflows = ({ id }) => {
    return client.graphql({ query: _getWorkflows, variables: { id } })
        .then(response => {
            const data = response.data.getWorkflows.workflows?.[0];
            if (!data) throw new Error('Workflow not found');
            return data;
        });
};

const upsertWorkflow = ({ input, input_delete }) => {
    return client.graphql({ query: _upsertWorkflow, variables: { input, input_delete } })
        .then(response => {
            return response.data.upsertWorkflow;
        });
};

const getDeals = () => {
    return client.graphql({ query: _getDeals, variables: { sorting: { field: 'deal_name', order: 'asc' } } })
        .then(response => {
            return [
                ...response.data.getDeals.deals
            ];
        });
};

const getRecruitmentSources = ({ parent_id } = {}) => {
    const filters = [
        ...(parent_id !== undefined ? [{ parent_id: { eq: parent_id } }] : [])
    ];
    return client.graphql({ query: _getRecruitmentSources, variables: { filters, sorting: { field: 'source_name', order: 'asc' } } })
        .then(response => {
            return [
                ...response.data.getRecruitmentSources.recruitment_sources
            ];
        });
};

const getSegments = () => {
    return client.graphql({ query: _getSegments, variables: { sorting: { field: 'name', order: 'asc' } } })
        .then(response => {
            return [
                ...response.data.getSegments.segments
            ];
        });
};

const getStudies = () => {
    return client.graphql({ query: _getStudies, variables: { sorting: { field: 'study_name', order: 'asc' } } })
        .then(response => {
            return [
                ...response.data.getStudies.studies
            ];
        });
};

const find_const = (id, c) => c.find(e => e.id === id || (!e.id && !id));

export default ({ id, links = [] }) => {
    const rootContext = useContext(RootContext);
    const find_deal_id = id => rootContext.get('deals')?.find(e => e.id === id);
    const find_recruiter_id = id => rootContext.get('recruiters')?.find(e => e.id === id);
    const find_recruitment_id = id => rootContext.get('recruitment_sources')?.find(e => e.id === id);
    const find_segment_id = id => rootContext.get('segments')?.find(e => e.id === id);
    const find_study_id = id => rootContext.get('studies')?.find(e => e.id === id);
    const find_agent = agent_name => rootContext.get('agent_settings')?.find(e => e.agent_name === agent_name);

    const isConstructed = useRef(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const navigate = useNavigate();

    // form state
    const [name, setName] = useState('');
    const [selectRecruitmentSources, setSelectRecruitmentSources] = useState(null);
    const [selectCampaigns, setSelectCampaigns] = useState(null);
    const [selectSegments, setSelectSegments] = useState(null);
    const [agentPrompt, setAgentPrompt] = useState(null);
    const [textAgentName, setTextAgentName] = useState(null);
    const [voiceAgentName, setVoiceAgentName] = useState(null);
    const [tasks, setTasks] = useState([]);
    const updateTask = task => {
        setTasks(old_tasks => {
            const new_tasks = _.cloneDeep(old_tasks);
            const idx = new_tasks.findIndex(f => f.id === task.id);
            if (idx !== -1) {
                new_tasks[idx] = task;
            } else {
                new_tasks.push(task);
            }
            return new_tasks;
        });
    }
    const [showTaskModal, setShowTaskModal] = useState(false);
    const [errors, setErrors] = useState({});
    const [isNameEditing, setIsNameEditing] = useState(id === 'new');
    const oldValues = useRef();

    const isPublishDisabled = useMemo(() => {
        // Only requirement for create is a name
        if (!id && name?.trim().length > 0) return false;
        const newValues = {
            agent_prompt: agentPrompt,
            name,
            selectCampaigns,
            selectRecruitmentSources,
            selectSegments,
            tasks,
            text_agent_name: textAgentName,
            voice_agent_name: voiceAgentName
        };
        const equal = _.isEqualWith(newValues, oldValues.current);
        return equal;
    });

    if (!isConstructed.current) {
        isConstructed.current = true;
        (async () => {
            if (!rootContext.get('deals')) {
                // Load up deals into RootContext
                await getDeals().then(data => rootContext.set('deals', data));
            }
            if (!rootContext.get('studies')) {
                // Load up studies into RootContext
                await getStudies().then(data => rootContext.set('studies', data));
            }
            if (!rootContext.get('recruitment_sources')) {
                // Load up recruitment_sources into RootContext
                await getRecruitmentSources().then(data => rootContext.set('recruitment_sources', data));
            }
            if (!rootContext.get('segments')) {
                // Load up segments into RootContext
                await getSegments().then(data => rootContext.set('segments', data));
            }
            if (id) {
                getWorkflows({ id })
                    .then(data => {
                        setName(data.name);
                        setAgentPrompt(data.agent_prompt);
                        setTextAgentName(data.text_agent_name);
                        setVoiceAgentName(data.voice_agent_name);
                        let subject_filters_json;
                        let _selectRecruitmentSources = null;
                        let _selectCampaigns = null;
                        let _selectSegments = null;
                        try { subject_filters_json = JSON.parse(data.subject_filters_json); } catch (_) {};
                        if (subject_filters_json?.[0]?.subject_recruitment_sources?.recruitment_id) {
                            const options = [];
                            const child_options = [];
                            (subject_filters_json[0].subject_recruitment_sources.recruitment_id.in || []).forEach(e => {
                                const rs = find_recruitment_id(e);
                                if (rs?.parent_id) {
                                    child_options.push({ id: e, parent_id: rs?.parent_id, source_name: rs?.source_name });
                                } else {
                                    options.push({ id: e, parent_id: rs?.parent_id, source_name: rs?.source_name });
                                }
                            });
                            setSelectRecruitmentSources(options.length > 0 ? options : null);
                            _selectRecruitmentSources = options.length > 0 ? options : null;
                            setSelectCampaigns(child_options.length > 0 ? child_options : null);
                            _selectCampaigns = child_options.length > 0 ? child_options : null;
                        }
                        if (subject_filters_json?.[0]?.segments?.id) {
                            const options = (subject_filters_json[0].segments.id.in || []).map(e => ({ id: e, name: find_segment_id(e)?.name }));
                            setSelectSegments(options.length > 0 ? options : null);
                            _selectSegments = options.length > 0 ? options : null;
                        }
                        let tasks_json = data.tasks_json;
                        try { tasks_json = JSON.parse(data.tasks_json); } catch (_) { tasks_json = []; };
                        setTasks(tasks_json);
                        oldValues.current = {
                            agent_prompt: data.agent_prompt,
                            name: data.name,
                            selectCampaigns: _.cloneDeep(_selectCampaigns),
                            selectRecruitmentSources: _.cloneDeep(_selectRecruitmentSources),
                            selectSegments: _.cloneDeep(_selectSegments),
                            tasks: _.cloneDeep(tasks_json),
                            text_agent_name: data.text_agent_name,
                            voice_agent_name: data.voice_agent_name
                        };
                        setIsLoaded(true);
                    });
            } else {
                setIsLoaded(true);
            }
        })();
        if (!rootContext.get('agent_settings')) {
            // Load up agent_settings into RootContext
            getAgentSettings()
                .then(data => {
                    // Dedupe by agent_name
                    const _data = _.uniqBy(data, e => e.agent_name);
                    rootContext.set('agent_settings', _data);
                });
        }
        if (!rootContext.get('recruiters')) {
            // Load up recruiters into RootContext
            // FIXME: setTimeout used here to have a microtick at least since setState is not allowed inline
            setTimeout(() => {
                rootContext.set('recruiters', [
                    { id: 1, name: 'Recruiter #1' },
                    { id: 2, name: 'Recruiter #2' },
                    { id: 3, name: 'Recruiter #3' }
                ]);
            }, 0);
        }
    }

    const getRSParents = useCallback(async () => {
        return rootContext.get('recruitment_sources')?.filter(e => !e.parent_id);
    });
    const getRSChildren = useCallback(async ({ parent_id }) => {
        const _parent_id = Array.isArray(parent_id) ? parent_id : [parent_id];
        return rootContext.get('recruitment_sources')?.filter(e => _parent_id.includes(e.parent_id));
    });

    // Check whether there are any children when recruitment_id changes
    const [isRSChildren, setIsRSChildren] = useState(false);
    useEffect(() => {
        let data;
        if (selectRecruitmentSources?.length > 0) {
            getRSChildren({ parent_id: selectRecruitmentSources.map(e => e.id) })
                .then(data => setIsRSChildren(data.length > 1));
            return;
        }
        setIsRSChildren(data?.length > 1);
    }, [selectRecruitmentSources]);
    console.log('isRSChildren', isRSChildren);

    const removeTask = (id) =>
        setTasks((prev) => prev.filter((t) => t.id !== id));

    const publish = async () => {
        const nextErrors = {};

        const nameTrimmed = (name ?? '').trim();

        if (!nameTrimmed) {
            nextErrors.name = 'Name is required';
        } else if (typeof isNameEditing !== 'undefined' && isNameEditing) {
            nextErrors.name = 'Please save the Name before continuing';
        }

        if (Object.keys(nextErrors).length) {
            setErrors(prev => ({ ...prev, ...nextErrors }));
            return;
        }

        // Composite of selectRecruitmentSources and selectCampaigns since they are both recruitment_source
        const recruitment_id = _.uniq([
            ...(selectCampaigns?.length > 0 ? selectCampaigns.map(e => e.id) : []),
            ...(selectRecruitmentSources?.length > 0 ? selectRecruitmentSources.map(e => e.id) : [])
        ]);

        const subject_filters_json = [];
        if (recruitment_id.length > 0 || selectSegments?.length > 0) {
            subject_filters_json.push({
                ...(recruitment_id.length > 0 ? { subject_recruitment_sources: { recruitment_id: { in: recruitment_id } } } : undefined),
                ...(selectSegments?.length > 0 ? { segments: { id: { in: selectSegments.map(e => e.id) } } } : undefined)
            });
        }
        console.log('subject_filters_json', subject_filters_json);

        try {
            await upsertWorkflow({
                input: {
                    workflows: {
                        agent_prompt: agentPrompt?.trim().length > 0 ? agentPrompt.trim() : null,
                        id,
                        name: nameTrimmed,
                        subject_filters_json: subject_filters_json?.length > 0 ? JSON.stringify(subject_filters_json) : null,
                        tasks_json: JSON.stringify(tasks),
                        text_agent_name: textAgentName?.trim().length > 0 ? textAgentName.trim() : null,
                        voice_agent_name: voiceAgentName?.trim().length > 0 ? voiceAgentName.trim() : null
                    },
                },
            });

            navigate('/app/customers/workflows');
        } catch (err) {
            console.error(err);
            setErrors(prev => ({
                ...prev,
                _global: 'Failed to save workflow. Please try again.',
            }));
        }
    };

    const get_voice_agents = useCallback(() => {
        const a = rootContext.get('agent_settings')?.filter(e => e.agent_modality === 'Voice') || [];
        // Make sure pre-existing choice is there
        if (voiceAgentName && !a.find(e => e.agent_name === voiceAgentName)) {
            // Add it if needed
            a.push({ agent_name: voiceAgentName });
        }
        return a;
    }, [voiceAgentName]);

    const get_text_agents = useCallback(() => {
        const a = rootContext.get('agent_settings')?.filter(e => e.agent_modality === 'Text') || [];
        // Make sure pre-existing choice is there
        if (textAgentName && !a.find(e => e.agent_name === textAgentName)) {
            // Add it if needed
            a.push({ agent_name: textAgentName });
        }
        return a;
    }, [textAgentName]);

    return (
        <div className="container-fluid px-4 position-relative">
            <Header path="/app/customers/">
                {links.length > 0 && <Breadcrumb className="mb-5 mx-3" links={links} />}
            </Header>

            {!isLoaded ? (
                <div className="text-center" style={{ marginTop: '5rem' }}>
                    <button className="btn btn-primary" type="button" disabled>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Loading...
                    </button>
                </div>
            ) : (
                <div >
                    {/* Title + subtitle */}
                    <div className="mb-5" style={{paddingLeft:"40px"}} >
                        <h3 className="text-start mb-1" style={{ color: "#2E2868", fontWeight: "bold" }}>
                            Workflow Builder
                        </h3>
                        <div className="text-muted">Design a workflow for subject engagement</div>
                        <hr />
                    </div>

                    <div className="px-5"><b><u>Workflow Settings</u></b></div>

                    <div className="row g-4 justify-content-center align-items-center builder-meta-row pt-4 px-5">
                        {/* Name */}
                        <div className="col-12 col-md-6 col-lg-3 d-flex align-items-center flex-nowrap">
                            {textField({
                                autoFocus: true,
                                className: 'col',
                                get: () => name,
                                getError: () => errors.name,
                                label: 'Name',
                                set: ({ value }) => { setName(value); value && value.trim().length > 0 && setErrors(prevState => ({ ...prevState, name: undefined }))}
                            })}
                        </div>

                        {/* Segment */}
                        <div className="col-12 col-md-6 col-lg-3">
                            {selectField({
                                className: 'col',
                                get: () => selectSegments,
                                getData: async () => rootContext.get('segments'),
                                isMulti: true,
                                set: ({ option }) => setSelectSegments(option.length > 0 ? option : null),
                                label: 'Segment',
                                label1Key: 'name',
                                name: 'id',
                                placeholder: 'No Segment'
                            })}
                        </div>

                        {/* Recruitment Source */}
                        <div className="col-12 col-md-6 col-lg-3 d-flex align-items-center flex-nowrap">
                            {selectField({
                                className: 'col',
                                extraKeys: ['parent_id'],
                                get: () => selectRecruitmentSources,
                                getData: () => getRSParents(),
                                getError: () => errors.recruitment_id,
                                isMulti: true,
                                set: ({ option }) => { setSelectRecruitmentSources(option.length > 0 ? option : null); setSelectCampaigns(null); },
                                label: 'Recruitment Source',
                                label1Key: 'source_name',
                                name: 'recruitment_id',
                                placeholder: 'No Recruitment Source'
                            })}
                        </div>

                        {/* Campaign */}
                        <div className="col-12 col-md-6 col-lg-3 d-flex align-items-center flex-nowrap">
                            {isRSChildren && (
                                selectField({
                                    className: 'col',
                                    get: () => selectCampaigns,
                                    getData: () => getRSChildren({ parent_id: selectRecruitmentSources.map(e => e.id) }),
                                    getError: () => errors.recruitment_id,
                                    isMulti: true,
                                    set: ({ option }) => setSelectCampaigns(option.length > 0 ? option : null),
                                    label: 'Campaign',
                                    label1Key: 'source_name',
                                    name: 'recruitment_id',
                                    placeholder: 'No Campaign'
                                })
                            )}
                        </div>

                        <div className="row mt-3">
                            <div className="col-12 col-md-6 col-lg-3 ps-0 pe-4">
                                <div className="mb-2"><b>Voice Agent</b></div>
                                <select
                                    className="form-select"
                                    defaultValue={voiceAgentName}
                                    onChange={e => setVoiceAgentName(e.target.value)}
                                >
                                    <option value="">No Agent</option>
                                    {get_voice_agents().map((e, i) => (<option key={i} value={e.agent_name}>{e.agent_name}</option>))}
                                </select>
                            </div>
                            <div className="col-12 col-md-6 col-lg-3 ps-0">
                                <div className="mb-2"><b>Text Agent</b></div>
                                <select
                                    className="form-select"
                                    defaultValue={textAgentName}
                                    onChange={e => setTextAgentName(e.target.value)}
                                >
                                    <option value="">No Agent</option>
                                    {get_text_agents().map((e, i) => (<option key={i} value={e.agent_name}>{e.agent_name}</option>))}
                                </select>
                            </div>
                        </div>
                        {(find_agent(voiceAgentName)?.supports_agent_prompt || find_agent(textAgentName)?.supports_agent_prompt || agentPrompt) &&
                            <div className="row mt-3">
                                <div className="col px-0">
                                    <div className="w-100"></div>
                                    <div className="mb-2"><b>Agent Prompt</b></div>
                                    <AutoTextarea
                                        className="form-control"
                                        defaultValue={agentPrompt}
                                        rows="5"
                                        onChange={e => setAgentPrompt(e.target.value)}
                                    >
                                    </AutoTextarea>
                                </div>
                            </div>
                        }

                    </div>

                    {/* <hr className="my-4" /> */}
                    <hr className="my-3 border-white opacity-100" />

                    <div className="px-5"><b><u>Tasks</u></b></div>

                    {/* Add task + helper */}
                    {!tasks.length && (
                        <div className="d-flex align-items-center gap-3 px-5">
                            <button
                                className="btn"
                                onClick={() => setShowTaskModal(true)}
                                style={{ background: "#351c75", color: "white", minWidth: 140 }}
                            >
                                + Add Task
                            </button>
                            <div className="text-muted">
                                <span className="me-2" style={{ fontSize: "1.5rem", color: "#351c75" }}>●</span>
                                Click Add Task to get started
                            </div>
                        </div>
                    )}

                    {showTaskModal && (
                        <TaskModal
                            {...(typeof showTaskModal === 'object' ? showTaskModal : undefined)}
                            onClose={() => setShowTaskModal(false)}
                            onSave={newTask => { updateTask(newTask); setShowTaskModal(false); }}
                            supportsTextIntro={find_agent(voiceAgentName)?.supports_text_intro || find_agent(textAgentName)?.supports_text_intro}
                            textAgentName={textAgentName}
                            voiceAgentName={voiceAgentName}
                        />
                    )}

                    {/* Timeline */}
                    <div className="wf-timeline mt-4">
                        <div className="wf-rail">
                            <span className="wf-rail-line" style={{ backgroundColor: "#351c75" }} />
                        </div>

                        <div className="wf-steps">
                            <Reorder.Group as="div" onReorder={setTasks} values={tasks}>
                                {tasks.map((t, idx) => {
                                    const isAutomatic = t.mode === "automatic" || t?.action;
                                    const actionLine = isAutomatic
                                        ? [
                                            t.action?.action ? <span className="text-body" key="action">{find_const(t.action.action, actions)?.label}</span> : null,
                                            t.action?.deal_id ? <React.Fragment key="deal_id"> to <span className="text-body">{find_deal_id(t.action.deal_id)?.deal_name}</span></React.Fragment> : null,
                                            t.action?.recruiter_id ? <React.Fragment key="recruiter_id"> to <span className="text-body">{find_recruiter_id(t.action.recruiter_id)?.name}</span></React.Fragment> : null,
                                            t.action?.study_id ? <React.Fragment key="study_id"> to <span className="text-body">{find_study_id(t.action.study_id)?.study_name}</span></React.Fragment> : null,
                                            ['engage_lead'].includes(t.action?.action) && (voiceAgentName || t.action?.agent_name) ? <React.Fragment key="agent_name"> using voice agent <span className="text-body">{voiceAgentName || t.action.agent_name}</span></React.Fragment> : null,
                                            ['engage_lead'].includes(t.action?.action) && (textAgentName || t.action?.text_agent_name) ? <React.Fragment key="text_agent_name"> {t.action?.agent_name ? 'or ' : ''}using text agent <span className="text-body">{textAgentName || t.action.text_agent_name}</span></React.Fragment> : null,
                                            ['engage_lead'].includes(t.action?.action) ? <React.Fragment key="skip_if_already_done"><span className="text-body">{t.action.skip_if_already_done ? ' and skip if already done' : ''}</span></React.Fragment> : null,
                                            t.action?.agent_precall_text ?
                                                <React.Fragment key="agent_precall_text">
                                                    <div className="mt-2">with precall text:</div>
                                                    <div
                                                        className="border-start border-primary p-2 my-2 text-muted"
                                                        style={{ maxHeight: '20rem', overflowY: 'auto' }}
                                                    >
                                                        {t.action.agent_precall_text?.split('\n').map((e, i) => <React.Fragment key={i}>{e}<br /></React.Fragment>)}
                                                    </div>
                                                </React.Fragment> : null,
                                            t.action?.text_intro ?
                                                <React.Fragment key="text_intro">
                                                    <div className="mt-2">with text intro:</div>
                                                    <div
                                                        className="border-start border-primary p-2 my-2 text-muted"
                                                        style={{ maxHeight: '20rem', overflowY: 'auto' }}
                                                    >
                                                        {t.action.text_intro?.split('\n').map((e, i) => <React.Fragment key={i}>{e}<br /></React.Fragment>)}
                                                    </div>
                                                </React.Fragment> : null,
                                            t.action?.followups?.length > 0 ?
                                                <React.Fragment key="followups">
                                                    {t.action?.followups.map((e, i) => (
                                                        <React.Fragment key={i}>
                                                            <div className="mt-2">
                                                                on delay of <span className="text-body">{delay.find(f => f.id === e.delay)?.label}</span>
                                                                {e.outcomes?.length > 0 ? <div key="outcomes">and outcome is any of <span className="text-secondary">{e.outcomes.join(', ')}</span></div> : null}
                                                                {e.segments?.length > 0 ? <div key="segments">and segment is any of <span className="text-secondary">{e.segments.map(e => find_segment_id(e)?.name).join(', ')}</span></div> : null}
                                                            </div>
                                                            <div>with followup text: {e.text ? '' : <span className="text-muted">Default</span>}</div>
                                                            {e.text && (
                                                                <div
                                                                    className="border-start border-primary p-2 my-2 text-muted"
                                                                    style={{ maxHeight: '20rem', overflowY: 'auto' }}
                                                                >
                                                                    {e.text?.split('\n').map((e, i) => <React.Fragment key={i}>{e}<br /></React.Fragment>)}
                                                                </div>
                                                            )}
                                                        </React.Fragment>
                                                    ))}
                                                </React.Fragment> : null,
                                            t.action?.message ?
                                                <React.Fragment key="message">
                                                    <div className="mt-2">with message:</div>
                                                    <div
                                                        className="border-start border-primary p-2 my-2 text-muted"
                                                    >
                                                        {t.action.message?.split('\n').map((e, i) => <React.Fragment key={i}>{e}<br /></React.Fragment>)}
                                                    </div>
                                                </React.Fragment> : null,
                                            t.action?.days ? ` on ${find_const(t.action.days, days)?.label}` : null,
                                            t.action?.days ? ` between ${find_const(t.action.start_time, times)?.label}` : null,
                                            t.action?.days ? ` and ${find_const(t.action.end_time, times)?.label}` : null,
                                            t.action?.outcomes?.length > 0 ? <div key="outcomes">until outcome is any of <span className="text-secondary">{t.action.outcomes.join(', ')}</span></div> : null,
                                            t.action?.segments?.length > 0 ? <div key="segments">until segment is any of <span className="text-secondary">{t.action.segments.map(e => find_segment_id(e)?.name).join(', ')}</span></div> : null
                                        ]
                                            .filter(Boolean)
                                        : null;

                                    return (
                                        <Reorder.Item
                                            as="div"
                                            key={t.id}
                                            value={t}
                                        >
                                            <div className="wf-step mb-3" key={t.id}>
                                                <span className="wf-dot" />
                                                <div className="row align-items-top">

                                                    <div className="col">
                                                        <div className="wf-title-wrap">
                                                            <span className="wf-title-index">{idx + 1}.</span>
                                                            <button
                                                                type="button"
                                                                className="wf-step-title"
                                                                onClick={() => setShowTaskModal({ data: t })}
                                                            >
                                                                {t.purpose}
                                                            </button>
                                                        </div>
                                                        {t.description?.trim() && (
                                                            <em className="wf-step-desc">{t.description}</em>
                                                        )}
                                                        <div className="wf-exec">Execution Mode: {isAutomatic ? "Automated by AVA" : "Manual"}</div>
                                                        {actionLine && <div className="wf-action-line" key={t.id}>{actionLine}</div>}
                                                    </div>

                                                    <div className="col-auto ms-auto">
                                                        <div className="btn-link text-decoration-none cursor-pointer" onClick={() => removeTask(t.id)}>Remove</div>
                                                    </div>

                                                </div>
                                            </div>
                                        </Reorder.Item>

                                    );
                                })}


                                {/* Button + Add Task aligned with the last step (in the left column) */}
                                {tasks.length > 0 && (
                                    <div className="wf-step mb-3">
                                        <span className="wf-dot" /> {/* The dot of the line */}

                                        <div className="row align-items-center">
                                            <div className="col">
                                                <button
                                                    className="btn"
                                                    onClick={() => setShowTaskModal(true)}
                                                    style={{ background: "#351c75", color: "white", minWidth: 140 }}
                                                >
                                                    + Add Task
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                            </Reorder.Group>
                        </div>
                    </div>

                    {/* Publish */}
                    <div className="text-center my-5">
                        <button
                            className="btn"
                            style={{ background: "#351c75", color: "white", minWidth: 140 }}
                            disabled={isPublishDisabled}
                            onClick={async () => {
                                await publish();
                            }}
                        >
                            Publish
                        </button>
                    </div>

                </div>
            )}

        </div>
    );
};
